
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowLeft,
  HiOutlineArrowUpRight,
  HiOutlineCalendarDays,
  HiOutlineVideoCamera,
  HiOutlinePlayCircle,
  HiOutlineChevronRight,
  HiOutlineRectangleStack,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import ResponsiveVideoPlayer from "../components/videos/ResponsiveVideoPlayer";
import RelatedVideos from "../components/videos/RelatedVideos";

/* ============================================
   VIDEO DETAILS PAGE
============================================ */

function VedioDetails() {
  const location = useLocation();
  const { i18n } = useTranslation();

  const video = location.state?.VideoData ?? null;
  const category = location.state?.category ?? null;

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const videoTitle = video
    ? isEnglish
      ? video.titleEn || video.titleAr
      : video.titleAr || video.titleEn
    : "";

  const categoryTitle = category
    ? isEnglish
      ? category.titleEn || category.titleAr
      : category.titleAr || category.titleEn
    : "";

  const categoryId =
    category?._id || video?.categoryId || null;

  const labels = {
    home: isEnglish ? "Home" : "الرئيسية",

    categories: isEnglish
      ? "Video Library"
      : "مكتبة الفيديوهات",

    eyebrow: isEnglish
      ? "LEGAL VIDEO LIBRARY"
      : "المكتبة القانونية المرئية",

    watch: isEnglish
      ? "Watch the Legal Explanation"
      : "شاهد الشرح القانوني",

    videoLabel: isEnglish
      ? "Legal Video"
      : "فيديو قانوني",

    youtube: isEnglish
      ? "Watch on YouTube"
      : "المشاهدة على يوتيوب",

    back: isEnglish
      ? "Back to Videos"
      : "العودة إلى الفيديوهات",

    missingTitle: isEnglish
      ? "Video Not Available"
      : "الفيديو غير متاح",

    missingText: isEnglish
      ? "Please select a video from the video library."
      : "يرجى اختيار الفيديو من المكتبة المرئية لعرض تفاصيله.",

    moreTitle: isEnglish
      ? "Explore More Legal Videos"
      : "اكتشف المزيد من الشروحات القانونية",

    moreDescription: isEnglish
      ? "Explore other legal subjects and video collections."
      : "تصفح المزيد من الموضوعات والمجموعات القانونية المرئية.",
  };

  const categoryPath = category
    ? "/VideosPage"
    : "/VideoCategoryPage";

  const categoryState = category
    ? { category }
    : undefined;

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ======================================
     MISSING VIDEO
  ====================================== */

  if (!video) {
    return (
      <>
        <Header active={5} />

        <main className="flex min-h-[75vh] items-center justify-center bg-[#F1F5F8] px-5 py-28 dark:bg-[#0B1C2A]">
          <div className="mx-auto max-w-[540px] text-center">

            <div className="mx-auto mb-7 flex h-20 w-20 items-center justify-center rounded-2xl border border-[#DCE5EB] bg-white text-[#C19B60] dark:border-white/10 dark:bg-[#1B3549]">
              <HiOutlineVideoCamera size={36} />
            </div>

            <h1 className="h3 text-[#19344D] dark:text-white">
              {labels.missingTitle}
            </h1>

            <p className="body-2 mt-5 text-[#62778A] dark:text-[#B5C8D6]">
              {labels.missingText}
            </p>

            <Link
              to="/VideoCategoryPage"
              className="mt-8 inline-flex min-h-[52px] items-center gap-3 rounded-xl bg-[#19344D] px-6 py-3 font-kufi text-sm font-semibold text-white transition-colors hover:bg-[#294961] dark:border dark:border-white/20 dark:bg-white/10 dark:hover:bg-white/15"
            >
              <HiOutlineArrowLeft
                size={19}
                className="rtl:-scale-x-100"
              />
              {labels.back}
            </Link>
          </div>
        </main>

        <Fotter />
      </>
    );
  }

  return (
    <>
      <Header active={5} />

      <main className="min-h-screen overflow-hidden bg-[#F1F5F8] text-[#19344D] dark:bg-[#0B1C2A] dark:text-white">

        {/* ======================================
            01 — COMPACT PAGE INTRO
        ====================================== */}

        <section
          className="
            relative overflow-hidden
            border-b border-white/10
            bg-[#102638]
            pt-28 pb-10
            text-white

            md:pt-32
            md:pb-12

            dark:bg-[#081927]
          "
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -end-32 -top-44 h-[440px] w-[440px] rounded-full bg-[#6589A4]/10 blur-[90px]"
          />

          <div className="container relative">

            {/* Breadcrumb */}
            <nav
              aria-label={isEnglish ? "Breadcrumb" : "مسار التنقل"}
              className="mb-9 flex flex-wrap items-center gap-2.5 text-white/55"
            >
              <Link
                to="/"
                className="font-kufi text-xs transition-colors hover:text-white"
              >
                {labels.home}
              </Link>

              <HiOutlineChevronRight
                size={14}
                className="rtl:-scale-x-100"
                aria-hidden="true"
              />

              <Link
                to="/VideoCategoryPage"
                className="font-kufi text-xs transition-colors hover:text-white"
              >
                {labels.categories}
              </Link>

              {category && (
                <>
                  <HiOutlineChevronRight
                    size={14}
                    className="rtl:-scale-x-100"
                    aria-hidden="true"
                  />

                  <Link
                    to="/VideosPage"
                    state={{ category }}
                    className="max-w-[220px] truncate font-kufi text-xs text-[#D7B77F] hover:text-white"
                  >
                    {categoryTitle}
                  </Link>
                </>
              )}
            </nav>

            {/* Eyebrow */}
            <div className="mb-5 flex items-center gap-3">
              <span
                aria-hidden="true"
                className="h-px w-9 bg-[#D7B77F]"
              />

              <span className="font-kufi text-xs font-semibold text-[#D7B77F]">
                {labels.eyebrow}
              </span>
            </div>

            {/* Video title */}
            <h1 className="h2 max-w-[1050px] text-white">
              {videoTitle}
            </h1>

            {/* Metadata */}
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">

              <div className="flex items-center gap-2 text-[#C6D5DF]">
                <HiOutlinePlayCircle
                  size={21}
                  className="text-[#D7B77F]"
                  aria-hidden="true"
                />

                <span className="font-kufi text-sm">
                  {labels.videoLabel}
                </span>
              </div>

              {video.date && (
                <div className="flex items-center gap-2 text-[#C6D5DF]">
                  <HiOutlineCalendarDays
                    size={19}
                    aria-hidden="true"
                  />

                  <span className="font-kufi text-sm">
                    {video.date}
                  </span>
                </div>
              )}

              {categoryTitle && (
                <div className="flex items-center gap-2 text-[#C6D5DF]">
                  <HiOutlineRectangleStack
                    size={19}
                    aria-hidden="true"
                  />

                  <span className="font-kufi text-sm">
                    {categoryTitle}
                  </span>
                </div>
              )}
            </div>

          </div>
        </section>

        {/* ======================================
            02 — VIDEO WATCH AREA
        ====================================== */}

        <section className="py-8 md:py-12 lg:py-14">
          <div className="container">

            <div
              className="
                grid grid-cols-1
                items-start gap-7

                lg:grid-cols-[minmax(0,1fr)_340px]
                lg:gap-8

                xl:grid-cols-[minmax(0,1fr)_370px]
              "
            >
              {/* ==================================
                  MAIN VIDEO
              ================================== */}

              <article className="min-w-0">

                <div
                  className="
                    overflow-hidden rounded-[22px]
                    border border-[#D9E4EC]
                    bg-white

                    shadow-[0_16px_45px_rgba(15,39,55,0.075)]

                    dark:border-white/10
                    dark:bg-[#142B3D]
                    dark:shadow-none
                  "
                >
                  {/* Player */}
                  <ResponsiveVideoPlayer
                    key={video.url || video._id}
                    link={video.url}
                    title={videoTitle}
                  />

                  {/* Video toolbar */}
                  <div
                    className="
                      flex flex-wrap
                      items-center justify-between
                      gap-5

                      border-t border-[#E1E9EF]
                      px-5 py-5

                      dark:border-white/10

                      sm:px-7 sm:py-6
                    "
                  >
                    <div className="flex items-center gap-3">

                      <div
                        className="
                          flex h-11 w-11
                          items-center justify-center
                          rounded-xl

                          bg-[#EDF3F7]
                          text-[#C19B60]

                          dark:bg-[#254458]
                          dark:text-[#D7B77F]
                        "
                      >
                        <HiOutlineVideoCamera
                          size={23}
                          aria-hidden="true"
                        />
                      </div>

                      <div>
                        <p className="font-kufi text-sm font-semibold text-[#19344D] dark:text-white">
                          {labels.watch}
                        </p>

                        <p className="mt-1 font-kufi text-xs text-[#71879A] dark:text-[#AFC3D1]">
                          AL NOMAN LAW OFFICE
                        </p>
                      </div>
                    </div>

                    {/* External video link */}
                    {video.url && (
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="
                          group inline-flex min-h-[44px]
                          items-center gap-2.5
                          rounded-xl

                          border border-[#D9E4EC]
                          bg-[#F5F8FA]

                          px-4 py-2.5

                          font-kufi text-xs font-semibold
                          text-[#19344D]

                          transition-all duration-300

                          hover:border-[#C19B60]/40
                          hover:bg-[#EDF3F7]

                          dark:border-white/10
                          dark:bg-white/[0.06]
                          dark:text-white
                          dark:hover:bg-white/[0.12]

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-[#C19B60]
                        "
                      >
                        {labels.youtube}

                        <HiOutlineArrowUpRight
                          size={18}
                          className="rtl:-scale-x-100 text-[#C19B60]"
                          aria-hidden="true"
                        />
                      </a>
                    )}
                  </div>
                </div>

                {/* Navigation below the player */}
                <div
                  className="
                    mt-6 flex flex-wrap
                    items-center justify-between
                    gap-4
                  "
                >
                  <Link
                    to={categoryPath}
                    state={categoryState}
                    onClick={scrollTop}
                    className="
                      group inline-flex
                      items-center gap-3

                      rounded-xl
                      border border-[#D9E4EC]
                      bg-white

                      px-5 py-3

                      font-kufi text-sm font-semibold
                      text-[#19344D]

                      transition-all duration-300

                      hover:border-[#C19B60]/40
                      hover:bg-[#EAF0F5]

                      dark:border-white/10
                      dark:bg-[#193448]
                      dark:text-white
                      dark:hover:bg-[#254458]
                    "
                  >
                    <HiOutlineArrowLeft
                      size={18}
                      className="rtl:-scale-x-100"
                      aria-hidden="true"
                    />

                    {labels.back}
                  </Link>
                </div>

              </article>

              {/* ==================================
                  VIDEOS SIDEBAR
              ================================== */}

              <aside className="min-w-0 lg:sticky lg:top-[105px]">
                <RelatedVideos
                  currentVideoId={video._id}
                  currentVideoUrl={video.url}
                  categoryId={categoryId}
                  category={category}
                />
              </aside>

            </div>
          </div>
        </section>

        {/* ======================================
            03 — EXPLORE MORE
        ====================================== */}

        <section
          className="
            border-t border-[#D9E4EC]
            bg-[#E9F0F5]
            py-14

            dark:border-white/10
            dark:bg-[#11283A]
          "
        >
          <div className="container">

            <div
              className="
                flex flex-col
                items-start justify-between
                gap-7

                lg:flex-row
                lg:items-center
              "
            >
              <div className="max-w-[680px]">
                <div className="mb-4 flex items-center gap-3">
                  <span className="h-px w-9 bg-[#C19B60]" />

                  <span className="font-kufi text-xs font-semibold text-[#A47D41] dark:text-[#D7B77F]">
                    {labels.categories}
                  </span>
                </div>

                <h2 className="h4 text-[#19344D] dark:text-white">
                  {labels.moreTitle}
                </h2>

                <p className="body-2 mt-4 text-[#62778A] dark:text-[#B5C8D6]">
                  {labels.moreDescription}
                </p>
              </div>

              <Link
                to="/VideoCategoryPage"
                onClick={scrollTop}
                className="
                  group inline-flex min-h-[52px]
                  shrink-0 items-center
                  gap-3 rounded-xl

                  bg-[#19344D]
                  px-6 py-3
                  text-white

                  transition-colors duration-300
                  hover:bg-[#294961]

                  dark:border dark:border-white/20
                  dark:bg-white/10
                  dark:hover:bg-white/15
                "
              >
                <span className="font-kufi text-sm font-semibold">
                  {labels.categories}
                </span>

                <HiOutlineArrowUpRight
                  size={19}
                  className="rtl:-scale-x-100 text-[#D7B77F]"
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Fotter />
    </>
  );
}

export default VedioDetails;
